/**
 * 語言切換器（🌐＋當前語言名；下拉列 LOCALES 全部語言——各自用本語言名顯示）
 * 深色站設計（dark tone 內建）；inline 變體供嵌入 Navbar。
 */
import { useEffect, useRef, useState } from 'react';
import { Globe } from 'lucide-react';
import { useI18n } from '../i18n';
import { LOCALES } from '../i18n/locales';

const LocaleSwitcher = ({ className = '' }: { className?: string }) => {
  const { locale, setLocale } = useI18n();
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const current = LOCALES.find((l) => l.code === locale) ?? LOCALES[0];

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div ref={boxRef} className={`relative ${className}`} dir="ltr">
      <button
        type="button"
        aria-label="Switch language"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 bg-jkd-gray-100 rounded-lg px-2.5 py-1.5 text-sm font-medium text-gray-300 hover:text-white transition-all duration-300"
      >
        <Globe className="w-4 h-4 text-jkd-gold" />
        <span className="hidden sm:inline">{current.nativeName}</span>
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1.5 min-w-[9.5rem] overflow-hidden rounded-xl border border-jkd-gray-200 bg-jkd-black py-1 shadow-lg">
          {LOCALES.map((l) => (
            <button
              key={l.code}
              type="button"
              lang={l.code}
              onClick={() => {
                setLocale(l.code);
                setOpen(false);
              }}
              className={`flex w-full items-center justify-between gap-3 px-3.5 py-2 text-left text-sm transition hover:bg-jkd-gray-100 ${
                l.code === locale ? 'font-bold text-jkd-gold' : 'text-gray-300'
              }`}
            >
              <span>{l.nativeName}</span>
              {l.code === locale && <span className="text-jkd-gold">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocaleSwitcher;
